// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Img, { ImagePresentationProps } from "@store/MobiusOverrides/Img/Img";
import { typographyTitleStyles } from "@store/Themes/common/mixins/typography";
import NotInYourCatalogBadge, {
    NotInYourCatalogBadgePresentationProps,
} from "@store/Library/Components/NotInYourCatalogBadge";
import RequestQuoteButton, { RequestQuoteButtonPresentationProps } from "@store/Library/Components/RequestQuoteButton";
import RequestPackagingAdviceButton, {
    RequestPackagingAdviceButtonPresentationProps,
} from "@store/Library/Components/RequestPackagingAdviceButton";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import { linkStyles } from "@store/Themes/common/mixins/link";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import scrollToId from "@toolkit/Framework/Common/Utilities/scrollToId";
import stripHtml from "@toolkit/Framework/Common/Utilities/stripHtml";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useContext } from "react";
import { css } from "styled-components";
import constants from "@store/constants";
import { siteWidthStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import Zone from "@insite/client-framework/Components/Zone";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import TruncatedTextByLine, {
    TruncatedTextByLinePresentationProps,
} from "@store/Library/Components/TruncatedTextByLine";
import ChevronRight from "@store/Library/Components/Toolkit/Icons/ChevronRight";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import getStickyHeaderHeight from "@store/Framework/Common/Utilities/getStickyHeaderHeight";

const breadcrumbZoneName = "Breadcrumb";

const enum fields {
    descriptionTargetAnchorId = "descriptionTargetAnchorId",
}

export interface ProductDetailsGenericProductBannerProps extends WidgetProps {
    fields: {
        [fields.descriptionTargetAnchorId]: string;
    };
}

export interface ProductDetailsGenericProductBannerStyles {
    wrapper?: InjectableCss;
    backgroundWrapper?: InjectableCss;
    breadcrumbWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: ImagePresentationProps;
    contentGridItem?: GridItemProps;
    contentWrapper?: InjectableCss;
    badgeWrapper?: InjectableCss;
    partNumbers?: ProductPartNumbersStyles;
    badge?: NotInYourCatalogBadgePresentationProps;
    title?: TypographyPresentationProps;
    description?: TruncatedTextByLinePresentationProps;
    readMoreLink?: LinkPresentationProps;
    buttonsWrapper?: InjectableCss;
    requestQuoteButton?: RequestQuoteButtonPresentationProps;
    requestPackagingAdviceButton?: RequestPackagingAdviceButtonPresentationProps;
}

export const productDetailsGenericProductBannerStyles: ProductDetailsGenericProductBannerStyles = {
    wrapper: {
        css: css`
            color: ${getColor("invert")};
            background: linear-gradient(280deg, #337761 50%, #00371e 50%);
        `,
    },
    breadcrumbWrapper: {
        css: css`
            ol {
                color: inherit !important;

                a {
                    color: inherit !important;

                    &::after {
                        background-color: currentColor !important;
                    }
                }
            }
        `,
    },
    backgroundWrapper: {
        css: css`
            ${siteWidthStyles([])}
            background: linear-gradient(280deg, #337761 4.64%, #00371e 82.03%);
        `,
    },
    innerWrapper: {
        css: css`
            ${siteWidthStyles()}
            display: flex;
            align-items: center;
            min-height: ${pxToRem(524)};
            padding-top: ${pxToRem(40)};
            padding-bottom: ${pxToRem(40)};
        `,
    },
    gridContainer: {
        gap: 32,
    },
    imageGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    image: {
        wrapperCss: css`
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: ${pxToRem(24)};
        `,
        css: css`
            max-width: 100%;
            max-height: ${pxToRem(444)};
            border-radius: inherit;
        `,
    },
    contentGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    contentWrapper: {
        css: css`
            padding: ${pxToRem(24)};
            border: 1px solid rgba(255, 255, 255, 0.2);
            background-color: rgba(255, 255, 255, 0.1);
            border-radius: ${pxToRem(24)};

            ${breakpoint("sm")} {
                padding: ${pxToRem(32)};
            }
        `,
    },
    badgeWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(8))}
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(12)};

            ${breakpoint("sm")} {
                flex-direction: row-reverse;
                align-items: center;
                justify-content: space-between;
            }
        `,
    },
    partNumbers: {
        wrapper: {
            css: css`
                color: inherit;
            `,
        },
    },
    title: {
        forwardAs: "h1",
        css: css`
            ${spacingStyles(null, pxToRem(16))}
            ${typographyTitleStyles(pxToRemArray([24, 30, null, null, 40]))}
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    description: {
        extendedStyles: {
            wrapper: {
                css: css`
                    ${spacingStyles(null, pxToRem(28))}
                    line-height: ${x => x.theme.design.typography.lineHeight.semi};
                `,
            },
        },
    },
    readMoreLink: {
        icon: {
            position: "right",
            iconProps: {
                color: "inherit",
                size: 18,
                src: ChevronRight,
                css: css`
                    margin-left: ${pxToRem(2)};
                `,
            },
        },
        css: css`
            ${linkStyles("invert", null, false, true)}
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(16, 20)};
        `,
    },
    requestQuoteButton: {
        extendedStyles: {
            button: {
                sizeVariant: "large",
                variant: "invertedAlt" as any,
            },
        },
    },
    requestPackagingAdviceButton: {
        extendedStyles: {
            button: {
                sizeVariant: "large",
                variant: "invertedOutlineAlt" as any,
            },
        },
    },
};

const styles = productDetailsGenericProductBannerStyles;

const ProductDetailsGenericProductBanner: FC<ProductDetailsGenericProductBannerProps> = ({
    fields: { descriptionTargetAnchorId },
    id,
}) => {
    const { product } = useContext(ProductContext);
    const { notFoundLargeImagePath } = useSettingsCollection().extensionCatalogSettings || {};
    const imageSrc = product.images?.[0]?.largeImagePath ?? product.largeImagePath ?? notFoundLargeImagePath;
    const breadcrumbZoneIsActive = useZoneIsActive(id, breadcrumbZoneName);
    const isMobile = useIsBreakpoint("xs");

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.backgroundWrapper}>
                {breadcrumbZoneIsActive && (
                    <StyledWrapper {...styles.breadcrumbWrapper}>
                        <Zone contentId={id} zoneName={breadcrumbZoneName} />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.innerWrapper}>
                    <GridContainer {...styles.gridContainer}>
                        <GridItem {...styles.imageGridItem}>
                            <Img {...styles.image} altText={product.imageAltText} src={imageSrc} />
                        </GridItem>
                        <GridItem {...styles.contentGridItem}>
                            <StyledWrapper {...styles.contentWrapper}>
                                <StyledWrapper {...styles.badgeWrapper}>
                                    <NotInYourCatalogBadge {...styles.badge} />
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        showCustomerName={false}
                                        showManufacturerItem={false}
                                        extendedStyles={styles.partNumbers}
                                    />
                                </StyledWrapper>
                                <Typography {...styles.title}>{product.productTitle}</Typography>
                                {Boolean(product.content?.htmlContent) && (
                                    <TruncatedTextByLine
                                        {...styles.description}
                                        alwaysShowChildren
                                        lineCount={isMobile ? 5 : 3}
                                        text={stripHtml(product.content!.htmlContent)}
                                    >
                                        {descriptionTargetAnchorId && (
                                            <>
                                                &nbsp;&nbsp;
                                                <Link
                                                    {...styles.readMoreLink}
                                                    onClick={() => {
                                                        scrollToId(descriptionTargetAnchorId, {
                                                            smooth: true,
                                                            padding: getStickyHeaderHeight(),
                                                        });
                                                    }}
                                                >
                                                    {translate("Read More")}
                                                </Link>
                                            </>
                                        )}
                                    </TruncatedTextByLine>
                                )}
                                <StyledWrapper {...styles.buttonsWrapper}>
                                    <RequestQuoteButton {...styles.requestQuoteButton} productId={product.id} />
                                    <RequestPackagingAdviceButton {...styles.requestPackagingAdviceButton} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsGenericProductBanner,
    definition: {
        displayName: "Generic Product Banner",
        group: constants.widgetGroup.productDetails,
        allowedContexts: [constants.pageType.productDetails],
        fieldDefinitions: [
            {
                name: fields.descriptionTargetAnchorId,
                displayName: "Description Target Anchor ID",
                editorTemplate: "TextField",
                defaultValue: "description",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
